import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import { clearPersistedQueryCache, queryClient } from '../lib/queryClient';
import api from '../services/api';

export interface AuthUser {
    id: number;
    name: string;
    email: string;
    must_change_password?: boolean;
    sector?: { id: number; name: string; slug?: string; color?: string };
}

export interface AuthPayload {
    user: AuthUser;
    roles: string[];
    permissions: string[];
}

interface AuthState {
    user: AuthUser | null;
    roles: string[];
    permissions: string[];
    status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';
    error: string | null;
    initialized: boolean;
}

const LS_AUTH = 'auth.cache';

/** Lê auth cacheado do localStorage — usado na hidratação inicial. */
const readCachedAuth = (): AuthPayload | null => {
    try {
        const raw = localStorage.getItem(LS_AUTH);
        return raw ? (JSON.parse(raw) as AuthPayload) : null;
    } catch {
        return null;
    }
};

const writeCachedAuth = (payload: AuthPayload | null) => {
    try {
        if (payload) localStorage.setItem(LS_AUTH, JSON.stringify(payload));
        else localStorage.removeItem(LS_AUTH);
    } catch {
        /* noop */
    }
};

/**
 * Sem sessão, nenhum dado de servidor pode sobreviver no browser (máquina compartilhada).
 * Exceção: queries do namespace 'public' (páginas compartilháveis sem login) — não carregam
 * dado de sessão e um clear cego cancelaria a request em voo, travando a página no skeleton.
 */
const clearServerCache = () => {
    try {
        queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'public' });
        queryClient.getMutationCache().clear();
        void clearPersistedQueryCache();
    } catch {
        /* noop */
    }
};

const cached = readCachedAuth();

const initialState: AuthState = {
    user: cached?.user ?? null,
    roles: cached?.roles ?? [],
    permissions: cached?.permissions ?? [],
    status: cached ? 'authenticated' : 'idle',
    error: null,
    initialized: false, // sempre false: o fetchMe valida em background e confirma
};

interface ApiErrorResponse {
    message?: string;
}

export const login = createAsyncThunk<AuthPayload, { email: string; password: string; turnstileToken?: string }, { rejectValue: string }>(
    'auth/login',
    async ({ email, password, turnstileToken }, { rejectWithValue }) => {
        try {
            const payload: Record<string, unknown> = { email, password };
            if (turnstileToken) payload.turnstile_token = turnstileToken;
            const { data } = await api.post<AuthPayload>('/api/login', payload);
            return data;
        } catch (err: unknown) {
            const message = (err as AxiosError<ApiErrorResponse>).response?.data?.message || 'Falha no login.';
            return rejectWithValue(message);
        }
    },
);

export interface AcceptInvitePayload {
    token: string;
    first_name: string;
    last_name: string;
    password: string;
    password_confirmation: string;
}

export const acceptInvite = createAsyncThunk<AuthPayload, AcceptInvitePayload, { rejectValue: string }>('auth/acceptInvite', async (payload, { rejectWithValue }) => {
    try {
        const { data } = await api.post<AuthPayload>('/api/invite/accept', payload);
        return data;
    } catch (err: unknown) {
        const message = (err as AxiosError<ApiErrorResponse>).response?.data?.message || 'Falha ao aceitar convite.';
        return rejectWithValue(message);
    }
});

export const fetchMe = createAsyncThunk<AuthPayload | null, void, { rejectValue: string }>('auth/fetchMe', async (_, { rejectWithValue }) => {
    try {
        const { data } = await api.get<AuthPayload>('/api/me');
        return data;
    } catch (err: unknown) {
        const apiError = err as AxiosError<ApiErrorResponse>;
        if (apiError.response?.status === 401) {
            return null;
        }
        return rejectWithValue(apiError.response?.data?.message || 'Erro ao buscar usuário.');
    }
});

export const logout = createAsyncThunk<void, void>('auth/logout', async () => {
    try {
        await api.post('/api/logout');
    } catch {
        /* ignore */
    }
});

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        const applyAuth = (state: AuthState, payload: AuthPayload) => {
            state.user = payload.user;
            state.roles = payload.roles;
            state.permissions = payload.permissions;
            state.status = 'authenticated';
            state.error = null;
            writeCachedAuth(payload);
        };

        const clearAuth = (state: AuthState) => {
            state.user = null;
            state.roles = [];
            state.permissions = [];
            state.status = 'unauthenticated';
            writeCachedAuth(null);
        };

        builder
            .addCase(login.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action: PayloadAction<AuthPayload>) => {
                applyAuth(state, action.payload);
                state.initialized = true;
            })
            .addCase(login.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.payload || 'Falha no login.';
            })
            .addCase(acceptInvite.fulfilled, (state, action: PayloadAction<AuthPayload>) => {
                applyAuth(state, action.payload);
                state.initialized = true;
            })
            .addCase(fetchMe.pending, (state) => {
                if (!state.initialized) state.status = 'loading';
            })
            .addCase(fetchMe.fulfilled, (state, action) => {
                if (action.payload) {
                    applyAuth(state, action.payload);
                } else {
                    clearAuth(state);
                    clearServerCache();
                }
                state.initialized = true;
            })
            .addCase(fetchMe.rejected, (state) => {
                clearAuth(state);
                state.initialized = true;
            })
            .addCase(logout.fulfilled, (state) => {
                clearAuth(state);
                clearServerCache();
            });
    },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
